import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import {
  Auth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  AuthErrorCodes,
} from '@angular/fire/auth';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { UserService } from '../shared/services/user.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { EnhancedLoadingService } from '../shared/services/enhanced-loading.service';
import { urls } from '../shared/constants';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-login',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private auth = inject(Auth);
  private userService = inject(UserService);
  private destroy = new Subject<void>();
  private snackBar = inject(SnackbarService);
  private loading = inject(EnhancedLoadingService);

  hide = true;
  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });
  contactUrl = environment.CONTACT_URL;

  ngOnInit(): void {
    this.auth.useDeviceLanguage();

    this.userService.authentication$
      .pipe(takeUntil(this.destroy))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          const returnUrl = this.router.parseUrl(this.router.url).queryParams[
            'returnUrl'
          ];
          const nextUrl = returnUrl || urls.agent;
          this.router.navigateByUrl(nextUrl);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.valid) {
      console.log('Form Data:', this.loginForm.value);
      try {
        this.loading.show('global');
        await signInWithEmailAndPassword(
          this.auth,
          this.loginForm.value.email!,
          this.loginForm.value.password!,
        );
      } catch (error: any) {
        console.error('Erro na autenticação: ', error);
        switch (error.code) {
          case AuthErrorCodes.INVALID_PASSWORD:
            this.snackBar.alert('Senha incorreta.');
            break;

          case AuthErrorCodes.INVALID_LOGIN_CREDENTIALS:
            this.snackBar.alert('Credenciais inválidas de login');
            break;

          case AuthErrorCodes.USER_DISABLED:
            this.snackBar.alert('Este usuário foi desabilitado.');
            break;

          default:
            this.snackBar.alert(
              `Erro de autenticação inesperado: ${error.message}`,
            );
            break;
        }
      } finally {
        this.loading.hide('global');
      }
    } else {
      Object.values(this.loginForm.controls).forEach((control) => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.snackBar.alert('Por favor, preencha todos os campos corretamente.');
    }
  }

  esqueceuSenha(): void {
    const emailControl = this.loginForm.controls.email;
    emailControl.markAsTouched();
    if (emailControl.invalid) {
      this.snackBar.alert('Por favor preencha seu email no campo acima.');
    } else {
      const email = emailControl.value!;
      this.loading.show('global');
      sendPasswordResetEmail(this.auth, email)
        .then(() => {
          this.snackBar.alert(
            'Caso exista uma conta vinculada ao e-mail informado, você receberá e-mail com as instruções para redefinição de senha.',
            -1,
          );
        })
        .catch((error) => {
          this.snackBar.alert(`Erro ao enviar email: ${error.message}`);
        })
        .finally(() => {
          this.loading.hide('global');
        });
    }
  }
}
